/**
 * Date/time helpers for the host's jukebox request dates. Everything here works
 * in the browser's local timezone: the host picks a local calendar day and a
 * local 15-minute slot, and we store the resulting instant as an ISO string.
 */

export const TIME_STEP_MINUTES = 15;
// How far ahead a host can schedule; guards against typos like 2062 for 2026.
export const MAX_DAYS_AHEAD = 365;

const pad = (n) => String(n).padStart(2, "0");

/** Local calendar day of `date` as the "YYYY-MM-DD" value <input type="date"> uses. */
export const toLocalDateInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Bounds for the date input: today through MAX_DAYS_AHEAD days from now. */
export const dateInputBounds = (now = new Date()) => {
  const max = new Date(now);
  max.setDate(max.getDate() + MAX_DAYS_AHEAD);
  return { min: toLocalDateInput(now), max: toLocalDateInput(max) };
};

/**
 * Combine a "YYYY-MM-DD" day and "HH:mm" time into a local Date, or null when
 * either part is missing or malformed.
 */
export const combineLocal = (dateValue, timeValue) => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue || "");
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue || "");
  if (!dateMatch || !timeMatch) return null;
  const [, y, m, d] = dateMatch.map(Number);
  const [, hh, mm] = timeMatch.map(Number);
  const date = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const timeLabel = (hours, minutes) =>
  new Date(2000, 0, 1, hours, minutes).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

/**
 * 15-minute time slots for the chosen day as { value: "HH:mm", label }.
 * On today, slots at or before `now` are left out so a past time can't be picked.
 */
export const timeOptionsFor = (dateValue, now = new Date()) => {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const value = `${pad(hours)}:${pad(mins)}`;
    const slot = combineLocal(dateValue, value);
    if (dateValue && (!slot || slot.getTime() <= now.getTime())) continue;
    options.push({ value, label: timeLabel(hours, mins) });
  }
  return options;
};

/** Short timezone name for `date` in the browser's zone, e.g. "PST". */
export const timeZoneAbbr = (date = new Date()) =>
  new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value || "";

/** IANA timezone of the browser, e.g. "America/Los_Angeles". */
export const timeZoneName = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "";

/** Human summary of an instant, e.g. "Tue, Nov 10, 2026 at 7:30 PM PST". */
export const formatSessionDate = (dateLike) => {
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  return `${day} at ${time}`;
};

/** Public share link for one jukebox day, e.g. https://www.siki.io/jukebox/<hostId>/<slotId>. */
export const jukeboxDayUrl = (hostId, slotId, origin = window.location.origin) =>
  `${origin}/jukebox/${encodeURIComponent(hostId)}/${encodeURIComponent(slotId)}`;

/**
 * Share a link with the native share sheet when there is one, else copy it.
 * Resolves "shared", "copied", or "cancelled" (the user closed the share sheet).
 */
export const shareLink = async ({ url, title, text }) => {
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ url, title, text });
      return "shared";
    } catch (error) {
      if (error?.name === "AbortError") return "cancelled";
      // Some browsers expose share() but refuse it (e.g. no user gesture); copy instead.
    }
  }
  await navigator.clipboard.writeText(url);
  return "copied";
};

/** Minute-precision key so two picks of the same slot count as duplicates. */
export const sessionKey = (dateLike) => Math.floor(new Date(dateLike).getTime() / 60000);

export const UNSCHEDULED_DAY = "unscheduled";

/** Short badge text for a day's submission counts, e.g. "3 pending · 7 total". */
export const submissionSummary = (counts) => {
  const total = counts?.total || 0;
  if (!total) return "No submissions yet";
  return `${counts.pending || 0} pending · ${total} total`;
};

/** Tab / filter label for a host day, or the unscheduled bucket. */
export const hostDayLabel = (day) =>
  day.id === UNSCHEDULED_DAY ? day.label || "Unscheduled / live" : formatSessionDate(day.startsAt);

/** The host's day that falls on today's local date, if any (the soonest one). */
export const todaysDay = (days, now = new Date()) =>
  (days || []).find((day) => {
    const start = new Date(day.startsAt);
    return (
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === now.getDate()
    );
  }) || null;
